import { useState, useEffect } from "react";
import { useAuth } from "@clerk/react";
import { useLocation } from "wouter";
import { format } from "date-fns";
import {
  ArrowLeft,
  Plus,
  Loader2,
  BookOpen,
  ClipboardList,
  ChevronDown,
  ChevronUp,
  Pencil,
  Trash2,
  ClipboardCheck,
  FileDown,
  Wand2,
  FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useProgramRegisterDetail,
  useCreateItem,
  useUpdateItem,
  useDeleteItem,
  useReorderItems,
  useDeleteDeliveryEvent,
  useUpsertEvaluation,
  useAiDraftEvaluation,
  statusBadgeClass,
  DELIVERY_STATUS_LABELS,
  type ProgramItem,
  type ProgramDeliveryEvent,
} from "@/lib/program-registration";
import { DeliveryEventForm } from "@/components/program-registration/delivery-event-form";
import { ImportReview } from "@/components/program-registration/import-review";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";

// ── Add / Edit item dialog ─────────────────────────────────────────────────────

interface ItemDialogProps {
  open: boolean;
  onClose: () => void;
  registerId: number;
  nextOrder: number;
  /** When provided the dialog is in edit mode */
  editItem?: ProgramItem;
}

function ItemDialog({
  open,
  onClose,
  registerId,
  nextOrder,
  editItem,
}: ItemDialogProps) {
  const { toast } = useToast();
  const createItem = useCreateItem(registerId);
  const updateItem = useUpdateItem(registerId);
  const isEdit = !!editItem;
  const isPending = createItem.isPending || updateItem.isPending;

  const [title, setTitle] = useState(editItem?.title ?? "");
  const [week, setWeek] = useState(editItem?.plannedWeek ?? "");
  const [lessonLabel, setLessonLabel] = useState(editItem?.plannedLessonLabel ?? "");
  const [content, setContent] = useState(editItem?.plannedContent ?? "");
  const [outcome, setOutcome] = useState(editItem?.outcomeOrContentDotPoint ?? "");
  const [resources, setResources] = useState(editItem?.plannedResources ?? "");
  const [notes, setNotes] = useState(editItem?.teacherNotes ?? "");
  const [titleTouched, setTitleTouched] = useState(false);

  // Re-seed form when editItem changes (e.g. opening edit on a different item)
  const prevEditId = useState(editItem?.id)[0];
  if (editItem && editItem.id !== prevEditId) {
    setTitle(editItem.title);
    setWeek(editItem.plannedWeek ?? "");
    setLessonLabel(editItem.plannedLessonLabel ?? "");
    setContent(editItem.plannedContent ?? "");
    setOutcome(editItem.outcomeOrContentDotPoint ?? "");
    setResources(editItem.plannedResources ?? "");
    setNotes(editItem.teacherNotes ?? "");
  }

  function resetForm() {
    setTitle("");
    setWeek("");
    setLessonLabel("");
    setContent("");
    setOutcome("");
    setResources("");
    setNotes("");
    setTitleTouched(false);
  }

  function handleClose() {
    if (!isEdit) resetForm();
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTitleTouched(true);
    if (!title.trim()) return;

    const payload = {
      title: title.trim(),
      plannedWeek: week.trim() || null,
      plannedLessonLabel: lessonLabel.trim() || null,
      plannedContent: content.trim() || null,
      outcomeOrContentDotPoint: outcome.trim() || null,
      plannedResources: resources.trim() || null,
      teacherNotes: notes.trim() || null,
    };

    if (isEdit) {
      updateItem.mutate(
        { itemId: editItem.id, data: payload },
        {
          onSuccess: () => {
            toast({ title: "Item updated" });
            handleClose();
          },
          onError: (err: unknown) => {
            toast({
              title: "Couldn't update item",
              description: getApiErrorMessage(err, "Please try again."),
              variant: "destructive",
            });
          },
        }
      );
    } else {
      createItem.mutate(
        { ...payload, plannedOrder: nextOrder },
        {
          onSuccess: () => {
            toast({ title: "Item added" });
            handleClose();
          },
          onError: (err: unknown) => {
            toast({
              title: "Couldn't add item",
              description: getApiErrorMessage(err, "Please try again."),
              variant: "destructive",
            });
          },
        }
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit program item" : "Add program item"}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          <div className="space-y-1.5">
            <Label htmlFor="item-title">
              Program item title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="item-title"
              placeholder="e.g. Introduction to narrative writing"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => setTitleTouched(true)}
              className={
                titleTouched && !title.trim() ? "border-destructive" : ""
              }
              autoFocus
            />
            {titleTouched && !title.trim() && (
              <p className="text-xs text-destructive">Title is required.</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="item-week">Planned week</Label>
              <Input
                id="item-week"
                placeholder="e.g. Week 1"
                value={week}
                onChange={(e) => setWeek(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="item-lesson">Planned lesson / block label</Label>
              <Input
                id="item-lesson"
                placeholder="e.g. Lesson 1, Activity 2, Topic 3"
                value={lessonLabel}
                onChange={(e) => setLessonLabel(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This is the label used in your program, not the actual period taught.
              </p>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="item-content">Planned content</Label>
            <Textarea
              id="item-content"
              placeholder="What do you plan to teach in this lesson/block?"
              rows={3}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="item-outcome">Outcome / content dot point</Label>
            <Textarea
              id="item-outcome"
              placeholder="Curriculum outcomes or content dot points addressed"
              rows={2}
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="item-resources">Planned resources</Label>
            <Input
              id="item-resources"
              placeholder="e.g. Textbook p.12, worksheet, slides"
              value={resources}
              onChange={(e) => setResources(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="item-notes">Teacher notes</Label>
            <Textarea
              id="item-notes"
              placeholder="Private planning notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEdit ? "Save changes" : "Add item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ── Program item row ───────────────────────────────────────────────────────────

interface ItemRowProps {
  item: ProgramItem;
  index: number;
  totalItems: number;
  registerId: number;
  deliveryCount: number;
  allItems: ProgramItem[];
  onLogDelivery: (item: ProgramItem) => void;
  onEdit: (item: ProgramItem) => void;
}

function ItemRow({
  item,
  index,
  totalItems,
  registerId,
  deliveryCount,
  allItems,
  onLogDelivery,
  onEdit,
}: ItemRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteItem = useDeleteItem(registerId);
  const reorderItems = useReorderItems(registerId);
  const { toast } = useToast();

  function handleDelete() {
    deleteItem.mutate(item.id, {
      onSuccess: () => toast({ title: "Item removed" }),
      onError: (err: unknown) => {
        toast({
          title: "Couldn't remove item",
          description: getApiErrorMessage(err, "Please try again."),
          variant: "destructive",
        });
      },
    });
    setConfirmDelete(false);
  }

  function moveItem(direction: "up" | "down") {
    const ids = allItems.map((i) => i.id);
    const from = index;
    const to = direction === "up" ? index - 1 : index + 1;
    if (to < 0 || to >= ids.length) return;
    const reordered = [...ids];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];
    reorderItems.mutate(reordered, {
      onError: (err: unknown) => {
        toast({
          title: "Couldn't reorder items",
          description: getApiErrorMessage(err, "Please try again."),
          variant: "destructive",
        });
      },
    });
  }

  const hasDetail =
    item.plannedContent ||
    item.outcomeOrContentDotPoint ||
    item.plannedResources ||
    item.teacherNotes;

  const isReordering = reorderItems.isPending;

  return (
    <>
      <div className="border rounded-lg bg-background group">
        <div className="flex items-start gap-2 p-3 sm:p-4">
          {/* Up/down reorder controls + index number */}
          <div className="flex flex-col items-center flex-shrink-0 mt-0.5 gap-0.5">
            <button
              type="button"
              title="Move up"
              disabled={index === 0 || isReordering}
              onClick={() => moveItem("up")}
              className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronUp className="h-3 w-3" />
            </button>
            <span className="w-5 text-center text-xs text-muted-foreground font-mono leading-none">
              {index + 1}
            </span>
            <button
              type="button"
              title="Move down"
              disabled={index === totalItems - 1 || isReordering}
              onClick={() => moveItem("down")}
              className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronDown className="h-3 w-3" />
            </button>
          </div>

          {/* Main content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {(item.plannedWeek || item.plannedLessonLabel) && (
                  <div className="text-xs text-muted-foreground mb-0.5">
                    {[item.plannedWeek, item.plannedLessonLabel]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                )}
                <div className="font-medium text-sm leading-snug">
                  {item.title}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {deliveryCount > 0 && (
                  <Badge
                    variant="outline"
                    className="text-xs px-1.5 py-0 text-green-700 border-green-200 bg-green-50"
                  >
                    {deliveryCount} delivery{deliveryCount !== 1 ? " events" : " event"}
                  </Badge>
                )}
              </div>
            </div>

            {/* Expanded detail */}
            {expanded && hasDetail && (
              <div className="mt-2 space-y-1.5 text-sm text-muted-foreground border-t pt-2">
                {item.plannedContent && (
                  <div>
                    <span className="font-medium text-foreground">Planned: </span>
                    {item.plannedContent}
                  </div>
                )}
                {item.outcomeOrContentDotPoint && (
                  <div>
                    <span className="font-medium text-foreground">Outcome: </span>
                    {item.outcomeOrContentDotPoint}
                  </div>
                )}
                {item.plannedResources && (
                  <div>
                    <span className="font-medium text-foreground">Resources: </span>
                    {item.plannedResources}
                  </div>
                )}
                {item.teacherNotes && (
                  <div>
                    <span className="font-medium text-foreground">Notes: </span>
                    {item.teacherNotes}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {hasDetail && (
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title={expanded ? "Collapse" : "Expand details"}
              >
                {expanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
            )}
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 px-2 text-xs hidden sm:flex"
              onClick={() => onLogDelivery(item)}
            >
              <ClipboardCheck className="mr-1 h-3 w-3" />
              Log delivery
            </Button>
            <button
              type="button"
              className="sm:hidden p-1.5 rounded hover:bg-muted text-muted-foreground transition-colors"
              title="Log delivery"
              onClick={() => onLogDelivery(item)}
            >
              <ClipboardCheck className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Edit item"
              onClick={() => onEdit(item)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Delete item"
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this item?</AlertDialogTitle>
            <AlertDialogDescription>
              Items with delivery events will be archived. Items without events
              will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Delivery event row ─────────────────────────────────────────────────────────

interface DeliveryEventRowProps {
  event: ProgramDeliveryEvent;
  itemTitle: string;
  registerId: number;
  onEdit: (event: ProgramDeliveryEvent) => void;
}

function DeliveryEventRow({
  event,
  itemTitle,
  registerId,
  onEdit,
}: DeliveryEventRowProps) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteEvent = useDeleteDeliveryEvent(registerId);
  const { toast } = useToast();
  const label =
    DELIVERY_STATUS_LABELS[event.deliveryStatus] ?? event.deliveryStatus;

  function handleDelete() {
    deleteEvent.mutate(event.id, {
      onSuccess: () => toast({ title: "Delivery event deleted" }),
      onError: (err: unknown) => {
        toast({
          title: "Couldn't delete event",
          description: getApiErrorMessage(err, "Please try again."),
          variant: "destructive",
        });
      },
    });
    setConfirmDelete(false);
  }

  return (
    <>
      <div className="border rounded-lg bg-background group">
        <div className="flex items-start gap-3 p-3 sm:p-4">
          {/* Date */}
          <div className="flex-shrink-0 text-center min-w-[3rem]">
            <div className="text-xs font-semibold text-foreground">
              {format(new Date(event.deliveryDate + "T12:00:00"), "d MMM")}
            </div>
            <div className="text-xs text-muted-foreground">
              {format(new Date(event.deliveryDate + "T12:00:00"), "yyyy")}
            </div>
          </div>

          <Separator orientation="vertical" className="h-full self-stretch" />

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadgeClass(event.deliveryStatus)}`}
              >
                {label}
              </span>
              {event.manualSessionLabel && (
                <span className="text-xs text-muted-foreground">
                  {event.manualSessionLabel}
                </span>
              )}
              {(event.period || event.className) && (
                <span className="text-xs text-muted-foreground">
                  {[event.period, event.className].filter(Boolean).join(" · ")}
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {itemTitle}
            </div>
            {event.reflectionNote && !expanded && (
              <p className="text-xs text-foreground mt-1 line-clamp-2">
                {event.reflectionNote}
              </p>
            )}

            {expanded && (
              <div className="mt-2 space-y-1.5 text-sm border-t pt-2">
                {event.reflectionNote && (
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Reflection
                    </span>
                    <p className="text-sm text-foreground mt-0.5">
                      {event.reflectionNote}
                    </p>
                  </div>
                )}
                {event.actualContentDelivered && (
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      What was delivered
                    </span>
                    <p className="text-sm text-foreground mt-0.5">
                      {event.actualContentDelivered}
                    </p>
                  </div>
                )}
                {event.remainingContentNote && (
                  <div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Remaining content
                    </span>
                    <p className="text-sm text-foreground mt-0.5">
                      {event.remainingContentNote}
                    </p>
                  </div>
                )}
                {event.differentiationSupportTypes &&
                  event.differentiationSupportTypes.length > 0 && (
                    <div>
                      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                        Support used
                      </span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {event.differentiationSupportTypes.map((t) => (
                          <span
                            key={t}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? (
                <ChevronUp className="h-3.5 w-3.5" />
              ) : (
                <ChevronDown className="h-3.5 w-3.5" />
              )}
            </button>
            <button
              type="button"
              title="Edit event"
              onClick={() => onEdit(event)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors opacity-0 group-hover:opacity-100"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              title="Delete event"
              onClick={() => setConfirmDelete(true)}
              className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors opacity-0 group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this delivery event?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── Detail page ───────────────────────────────────────────────────────────────

interface Props {
  params: { id: string };
}

export default function ProgramRegistrationDetail({ params }: Props) {
  const [, setLocation] = useLocation();
  const registerId = parseInt(params.id, 10);
  const { data, isLoading, error } = useProgramRegisterDetail(registerId);
  const { toast } = useToast();
  const { getToken } = useAuth();

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ProgramItem | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [deliveryItem, setDeliveryItem] = useState<ProgramItem | null>(null);
  const [editingEvent, setEditingEvent] = useState<ProgramDeliveryEvent | null>(null);
  const [exportWarningOpen, setExportWarningOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Evaluation tab state
  const [tabValue, setTabValue] = useState("items");
  const [aiEvalDialogOpen, setAiEvalDialogOpen] = useState(false);
  const [evalReflection, setEvalReflection] = useState("");
  const [evalWhatWorked, setEvalWhatWorked] = useState("");
  const [evalWhatChanged, setEvalWhatChanged] = useState("");
  const [evalDiffSummary, setEvalDiffSummary] = useState("");
  const [evalNextTime, setEvalNextTime] = useState("");

  const upsertEvaluation = useUpsertEvaluation(registerId);
  const aiDraftEvaluation = useAiDraftEvaluation(registerId);

  // Seed evaluation form from server whenever saved data changes
  const evaluationUpdatedAt = data?.evaluation?.updatedAt;
  useEffect(() => {
    if (data?.evaluation) {
      setEvalReflection(data.evaluation.overallReflection ?? "");
      setEvalWhatWorked(data.evaluation.whatWorkedWell ?? "");
      setEvalWhatChanged(data.evaluation.whatChangedFromPlannedProgram ?? "");
      setEvalDiffSummary(data.evaluation.differentiationSupportSummary ?? "");
      setEvalNextTime(data.evaluation.suggestedUpdatesForNextTime ?? "");
    }
  }, [evaluationUpdatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveEvaluation = () => {
    upsertEvaluation.mutate(
      {
        overallReflection: evalReflection.trim() || null,
        whatWorkedWell: evalWhatWorked.trim() || null,
        whatChangedFromPlannedProgram: evalWhatChanged.trim() || null,
        differentiationSupportSummary: evalDiffSummary.trim() || null,
        suggestedUpdatesForNextTime: evalNextTime.trim() || null,
        aiDraftUsed: aiDraftEvaluation.isSuccess,
        teacherEdited: true,
      },
      {
        onSuccess: () => toast({ title: "Evaluation saved" }),
        onError: (err: unknown) =>
          toast({
            title: "Couldn't save evaluation",
            description: getApiErrorMessage(err, "Please try again."),
            variant: "destructive",
          }),
      }
    );
  };

  const handleAiDraftConfirm = async () => {
    setAiEvalDialogOpen(false);
    setTabValue("evaluation");
    try {
      const result = await aiDraftEvaluation.mutateAsync();
      setEvalReflection(result.overallReflection);
      setEvalWhatWorked(result.whatWorkedWell);
      setEvalWhatChanged(result.whatChangedFromPlannedProgram);
      setEvalDiffSummary(result.differentiationSupportSummary);
      setEvalNextTime(result.suggestedUpdatesForNextTime);
      toast({ title: "AI draft ready — review and save when you're happy" });
    } catch (err: unknown) {
      toast({
        title: "AI draft failed",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleExportConfirm = async () => {
    setExportWarningOpen(false);
    setIsExporting(true);
    try {
      const token = await getToken();
      const res = await fetch(`/api/program-registers/${registerId}/export`, {
        headers: {
          Authorization: `Bearer ${token ?? ""}`,
          "X-App-Origin": window.location.hostname,
        },
      });
      if (!res.ok) throw new Error("Export failed");
      const html = await res.text();
      const win = window.open();
      if (win) { win.document.write(html); win.document.close(); }
    } catch (err: unknown) {
      toast({
        title: "Export failed",
        description: getApiErrorMessage(err, "Could not generate export. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (isNaN(registerId)) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        Invalid register ID.
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="border-b px-4 sm:px-6 py-4 bg-white sticky top-0 z-10">
          <button
            type="button"
            onClick={() => setLocation("/app/program-registration")}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to registers
          </button>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 p-8 text-center">
          <BookOpen className="h-12 w-12 text-muted-foreground/30 mb-3" />
          <p className="text-muted-foreground text-sm">Register not found.</p>
        </div>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const { register, items, events } = data;

  // Build a map: itemId → delivery events
  const eventsByItem = new Map<number, typeof events>();
  for (const ev of events) {
    const list = eventsByItem.get(ev.programItemId) ?? [];
    list.push(ev);
    eventsByItem.set(ev.programItemId, list);
  }

  // Map itemId → title for delivery log
  const itemTitleById = new Map(items.map((i) => [i.id, i.title]));

  // Sort events newest first for the delivery log
  const sortedEvents = [...events].sort(
    (a, b) =>
      new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime()
  );

  const meta = [register.subject, register.yearGroup, register.term]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="border-b px-4 sm:px-6 py-4 bg-white sticky top-0 z-10">
        <button
          type="button"
          onClick={() => setLocation("/app/program-registration")}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Program Registration
        </button>

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight break-words">
              {register.title}
            </h1>
            {meta && (
              <p className="text-sm text-muted-foreground mt-0.5">{meta}</p>
            )}
            {register.schoolYear && (
              <Badge variant="outline" className="mt-1 text-xs px-1.5 py-0">
                {register.schoolYear}
              </Badge>
            )}
          </div>

          {/* Export / AI buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setExportWarningOpen(true)}
              disabled={isExporting}
            >
              {isExporting ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <FileDown className="mr-1.5 h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {isExporting ? "Exporting…" : "Export PDF"}
              </span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setTabValue("evaluation");
                setAiEvalDialogOpen(true);
              }}
              disabled={aiDraftEvaluation.isPending}
              title="Draft the Overall Evaluation using AI"
            >
              {aiDraftEvaluation.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="mr-1.5 h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {aiDraftEvaluation.isPending ? "Drafting…" : "AI evaluation draft"}
              </span>
            </Button>
          </div>
        </div>

        {register.description && (
          <p className="text-sm text-muted-foreground mt-2 border-t pt-2">
            {register.description}
          </p>
        )}

        {/* Stats */}
        <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
          <span>
            <strong className="text-foreground">{items.length}</strong>{" "}
            item{items.length !== 1 ? "s" : ""}
          </span>
          <span>
            <strong className="text-foreground">{events.length}</strong>{" "}
            delivery event{events.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-y-auto">
        <Tabs value={tabValue} onValueChange={setTabValue} className="h-full flex flex-col">
          <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-4 sm:px-6 h-10 gap-1">
            <TabsTrigger
              value="items"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-sm"
            >
              <ClipboardList className="mr-1.5 h-3.5 w-3.5" />
              Program items
              {items.length > 0 && (
                <span className="ml-1.5 text-xs bg-muted rounded-full px-1.5">
                  {items.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="delivery"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-sm"
            >
              <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />
              Delivery log
              {events.length > 0 && (
                <span className="ml-1.5 text-xs bg-muted rounded-full px-1.5">
                  {events.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="evaluation"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none text-sm"
            >
              <FileText className="mr-1.5 h-3.5 w-3.5" />
              Evaluation
              {data.evaluation && (
                <span className="ml-1.5 text-xs bg-green-100 text-green-700 rounded-full px-1.5">
                  ✓
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Items tab */}
          <TabsContent
            value="items"
            className="flex-1 overflow-y-auto p-4 sm:p-6 mt-0"
          >
            {/* Action bar */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                size="sm"
                onClick={() => setAddItemOpen(true)}
              >
                <Plus className="mr-1.5 h-4 w-4" />
                Add item
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setImportOpen(true)}
              >
                <ClipboardList className="mr-1.5 h-4 w-4" />
                Paste/import program items
              </Button>
            </div>

            {/* Items list */}
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center max-w-sm mx-auto">
                <ClipboardList className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">
                  No program items yet
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Add items manually or use the buttons above to paste and
                  import in bulk.
                </p>
                <Button size="sm" onClick={() => setAddItemOpen(true)}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Add item
                </Button>
              </div>
            ) : (
              <div className="space-y-1.5 max-w-3xl">
                {items.map((item, idx) => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    index={idx}
                    totalItems={items.length}
                    registerId={registerId}
                    deliveryCount={eventsByItem.get(item.id)?.length ?? 0}
                    allItems={items}
                    onLogDelivery={(i) => setDeliveryItem(i)}
                    onEdit={(i) => setEditingItem(i)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Delivery log tab */}
          <TabsContent
            value="delivery"
            className="flex-1 overflow-y-auto p-4 sm:p-6 mt-0"
          >
            {events.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center max-w-sm mx-auto">
                <ClipboardCheck className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm font-medium text-foreground mb-1">
                  No delivery events yet
                </p>
                <p className="text-xs text-muted-foreground">
                  Go to the Program items tab and click "Log delivery" on any
                  item to record how it went.
                </p>
              </div>
            ) : (
              <div className="space-y-1.5 max-w-3xl">
                {sortedEvents.map((ev) => (
                  <DeliveryEventRow
                    key={ev.id}
                    event={ev}
                    itemTitle={itemTitleById.get(ev.programItemId) ?? "—"}
                    registerId={registerId}
                    onEdit={(ev) => setEditingEvent(ev)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Evaluation tab */}
          <TabsContent
            value="evaluation"
            className="flex-1 overflow-y-auto p-4 sm:p-6 mt-0"
          >
            <div className="max-w-2xl space-y-5">
              <div>
                <h2 className="text-sm font-semibold">Overall Evaluation</h2>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Reflect on how this program was delivered. Nothing is shared
                  or saved automatically — click Save when you're ready.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="eval-reflection">Overall reflection</Label>
                <Textarea
                  id="eval-reflection"
                  placeholder="How did this program go overall?"
                  value={evalReflection}
                  onChange={(e) => setEvalReflection(e.target.value)}
                  rows={4}
                  className="resize-y"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="eval-what-worked">What worked well</Label>
                <Textarea
                  id="eval-what-worked"
                  placeholder="Which aspects went particularly well?"
                  value={evalWhatWorked}
                  onChange={(e) => setEvalWhatWorked(e.target.value)}
                  rows={3}
                  className="resize-y"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="eval-what-changed">
                  What changed from the planned program
                </Label>
                <Textarea
                  id="eval-what-changed"
                  placeholder="What was different from your original plan?"
                  value={evalWhatChanged}
                  onChange={(e) => setEvalWhatChanged(e.target.value)}
                  rows={3}
                  className="resize-y"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="eval-diff-summary">
                  Differentiation / support summary
                </Label>
                <Textarea
                  id="eval-diff-summary"
                  placeholder="What supports or adjustments were used across the program?"
                  value={evalDiffSummary}
                  onChange={(e) => setEvalDiffSummary(e.target.value)}
                  rows={3}
                  className="resize-y"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="eval-next-time">
                  Suggested updates for next time
                </Label>
                <Textarea
                  id="eval-next-time"
                  placeholder="What would you adjust if you ran this program again?"
                  value={evalNextTime}
                  onChange={(e) => setEvalNextTime(e.target.value)}
                  rows={3}
                  className="resize-y"
                />
              </div>

              <div className="flex items-center gap-3 pt-1">
                <Button
                  onClick={handleSaveEvaluation}
                  disabled={upsertEvaluation.isPending}
                  size="sm"
                >
                  {upsertEvaluation.isPending && (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  )}
                  Save evaluation
                </Button>
                {data.evaluation && (
                  <span className="text-xs text-muted-foreground">
                    Last saved{" "}
                    {format(
                      new Date(data.evaluation.updatedAt),
                      "d MMM yyyy, h:mm a"
                    )}
                  </span>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <ItemDialog
        open={addItemOpen}
        onClose={() => setAddItemOpen(false)}
        registerId={registerId}
        nextOrder={items.length}
      />

      {editingItem && (
        <ItemDialog
          key={editingItem.id}
          open={true}
          onClose={() => setEditingItem(null)}
          registerId={registerId}
          nextOrder={items.length}
          editItem={editingItem}
        />
      )}

      <ImportReview
        open={importOpen}
        onClose={() => setImportOpen(false)}
        registerId={registerId}
        existingItemCount={items.length}
        existingSourceFile={
          register.sourceFileObjectKey && register.sourceFileName
            ? { fileName: register.sourceFileName, objectKey: register.sourceFileObjectKey }
            : null
        }
      />

      {deliveryItem && (
        <DeliveryEventForm
          open={true}
          onClose={() => setDeliveryItem(null)}
          registerId={registerId}
          item={deliveryItem}
        />
      )}

      {editingEvent && (() => {
        const editItem = items.find((i) => i.id === editingEvent.programItemId);
        return editItem ? (
          <DeliveryEventForm
            key={editingEvent.id}
            open={true}
            onClose={() => setEditingEvent(null)}
            registerId={registerId}
            item={editItem}
            event={editingEvent}
          />
        ) : null;
      })()}

      <AlertDialog open={exportWarningOpen} onOpenChange={setExportWarningOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Before you export</AlertDialogTitle>
            <AlertDialogDescription>
              Check that your Program Register does not include student names,
              diagnoses, behaviour notes, parent/carer details, or other
              sensitive student information in the free-text fields (such as
              actual content delivered or reflection notes).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleExportConfirm}>
              Open export
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={aiEvalDialogOpen} onOpenChange={setAiEvalDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Draft evaluation with AI</AlertDialogTitle>
            <AlertDialogDescription>
              The AI will use your program data — program items, delivery
              events, reflection notes, and differentiation labels — to draft an
              overall evaluation. No student names, diagnoses, NCCD details, or
              contact notes will be sent. Review and edit the draft before
              saving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleAiDraftConfirm}>
              Generate draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
